import { useMutation } from '@apollo/client';
import { Button } from 'antd';
import { CancelButton, ConfirmButton } from 'components/common/DefaultFormButton';
import DefaultModal from 'components/common/DefaultModal';
import { INPUT_TYPE } from 'components/common/Input/types';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { ERRORS } from 'constants/notify';
import { usStatesSelectOptions } from 'constants/usStates';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UPDATE_TRANSACTION } from 'services/graphql/mutations/transactions';
import { GeneralFunction } from 'types/common';
import { IAddress, ITransaction } from 'types/transaction';
import { defaultNotification } from 'utils';
import * as Yup from 'yup';

const addressValidation = {
  address1: Yup.string().max(46, 'Maximum length is 46').required('Required'),
  address2: Yup.string().max(46, 'Maximum length is 46').notRequired(),
  city: Yup.string().max(50, 'Maximum length is 50').required('Required'),
  state: Yup.string().nullable().required('Required'),
  zip: Yup.string()
    .matches(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/, 'Wrong format')
    .required('Required'),
};

const DocumentReturnLocationModal: React.FC<{
  visible?: boolean;
  transaction?: ITransaction;
  closeModal: GeneralFunction;
  refetchTransactionData?: GeneralFunction;
}> = ({ visible, transaction, closeModal, refetchTransactionData }) => {
  const documentReturnAddress = useMemo(() => transaction?.returnShippingAddress, [transaction]);
  const transactionAddress = useMemo(() => transaction?.location?.address, [transaction]);
  // const [locationSetting, setLocationSetting] = useState<'default' | 'addNew'>();

  const formikRef = useRef<any>();

  const [initValues, setInitValues] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });

  // useEffect(() => {
  //   if (formatAddress(transactionAddress) === formatAddress(documentReturnAddress)) {
  //     setLocationSetting('default');
  //   } else {
  //     setLocationSetting('addNew');
  //   }
  // }, [transactionAddress, documentReturnAddress]);
  useEffect(() => {
    if (documentReturnAddress) setInitValues(documentReturnAddress);
  }, [documentReturnAddress]);

  const onUseDefaultLocationClick = () => {
    if (transactionAddress && !!formikRef.current) {
      formikRef.current?.handleReset?.();
      setInitValues(transactionAddress);
    }
  };

  const inputProps: InputProps[] = [
    {
      label: 'Address Line 1',
      name: 'address1',
      addressSuggestion: true,
      addressSuggestionSubFields: {
        address1: 'address1',
        city: 'city',
        zip: 'zip',
        state: 'state',
      },
    },
    { label: 'Address Line 2', name: 'address2' },
    { label: 'City', name: 'city' },
    {
      label: 'Zip code',
      name: 'zip',
      sideFields: [
        {
          size: 12,
          label: 'State',
          name: 'state',
          type: INPUT_TYPE.SELECT,
          inputOptions: usStatesSelectOptions,
        },
      ],
    },
  ];

  // const dataFields: DataFieldProps[] = useMemo(
  //   () => [
  //     {
  //       label: 'Address Line 1',
  //       value: documentReturnAddress?.address1,
  //     },
  //     { label: 'Address Line 2', value: documentReturnAddress?.address2 },
  //     { label: 'City', value: documentReturnAddress?.city },
  //     {
  //       label: 'Zip code',
  //       value: documentReturnAddress?.zip,
  //       sideFields: [
  //         {
  //           label: 'State',
  //           value: documentReturnAddress?.state,
  //           size: 12,
  //         },
  //       ],
  //     },
  //   ],
  //   [documentReturnAddress],
  // );

  const [updateTransaction, { loading }] = useMutation(UPDATE_TRANSACTION);
  const onSubmit = async (v: IAddress) => {
    try {
      await updateTransaction({
        variables: {
          filter: {
            id: transaction?.id,
          },
          updatedDoc: {
            returnShippingAddress: v,
          },
        },
      });
      defaultNotification({ success: 'Change document return address successfully!' });
      refetchTransactionData?.();
      closeModal();
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };
  return (
    <DefaultModal
      props={{ visible, title: 'Edit Default Return Documents Adddress', destroyOnClose: true, onCancel: closeModal }}>
      <div className='px-4'>
        <div className='mb-3'>
          {/* <InputComponent
            type={INPUT_TYPE.RADIO}
            name='locationOption'
            inputOptions={[
              { value: 'default', label: 'Default Location Address' },
              { value: 'addNew', label: 'Add New Location Address' },
            ]}
            value={locationSetting}
            onChange={onLocationSettingChange}
          /> */}
          <Button className='!mb-0' onClick={onUseDefaultLocationClick}>
            Use Default Location
          </Button>
        </div>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          onSubmit={onSubmit}
          initialValues={initValues}
          validationSchema={Yup.object(addressValidation)}>
          <Form>
            <InputFieldsGenerator formFields={inputProps} />
            <div className='mt-3 flex items-center'>
              <ConfirmButton htmlType='submit' isLoading={loading}>
                Save
              </ConfirmButton>
              <CancelButton onClick={closeModal}>Cancel</CancelButton>
            </div>
          </Form>
        </Formik>
      </div>
    </DefaultModal>
  );
};

export default React.memo(DocumentReturnLocationModal);
